export type StoredAuthUser = { id: string; email: string; name: string }
export type StoredAuth = { accessToken: string; user: StoredAuthUser | null; expiresAt?: number; refreshToken?: string; refreshExpiresAt?: number }

export const AUTH_STORAGE_KEY = 'lulla.server-auth.v2'
const legacyTokenKey = 'lulla.server-token.v1'
const legacyUserKey = 'lulla.server-user.v1'

const validUser = (value: unknown): StoredAuthUser | null => {
  if (!value || typeof value !== 'object') return null
  const user = value as Partial<StoredAuthUser>
  return typeof user.id === 'string' && typeof user.email === 'string' && typeof user.name === 'string' ? { id: user.id, email: user.email, name: user.name } : null
}
const clearLegacy = () => {
  try { sessionStorage.removeItem(legacyTokenKey); sessionStorage.removeItem(legacyUserKey) } catch { /* Storage can be unavailable. */ }
}

/** One record keeps account identity and credentials together across app restarts and OTA reloads. */
export function saveStoredAuth(auth: StoredAuth | null): boolean {
  try {
    if (auth) localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth))
    else localStorage.removeItem(AUTH_STORAGE_KEY)
    clearLegacy()
    return true
  } catch { return false }
}

export function readStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (raw) {
      const auth = JSON.parse(raw) as Partial<StoredAuth>
      if (typeof auth.accessToken !== 'string' || !auth.accessToken) return null
      return { accessToken: auth.accessToken, user: validUser(auth.user),
        ...(typeof auth.expiresAt === 'number' && Number.isFinite(auth.expiresAt) ? { expiresAt: auth.expiresAt } : {}),
        ...(typeof auth.refreshToken === 'string' && auth.refreshToken ? { refreshToken: auth.refreshToken } : {}),
        ...(typeof auth.refreshExpiresAt === 'number' && Number.isFinite(auth.refreshExpiresAt) ? { refreshExpiresAt: auth.refreshExpiresAt } : {}) }
    }
    // Existing installations keep their current login when the new storage format first arrives.
    const accessToken = sessionStorage.getItem(legacyTokenKey)
    if (!accessToken) return null
    let user: StoredAuthUser | null = null
    try { user = validUser(JSON.parse(sessionStorage.getItem(legacyUserKey) || 'null')) } catch { /* Preserve a usable legacy token. */ }
    const migrated = { accessToken, user }
    saveStoredAuth(migrated)
    return migrated
  } catch { return null }
}
